import type { ComponentProps, ReactElement } from 'react';
import styled from '@emotion/styled';
import * as tokens from '@tokens';

import { Breadcrumb } from './Breadcrumb';
import { useStrings } from '@hooks/useStrings';

export interface BreadcrumbsProps {
  /**
   * A list of `Breadcrumbs.Item` components.
   */
  children:
    | ReactElement<ComponentProps<typeof Breadcrumb>, 'Breadcrumb'>[]
    | ReactElement<ComponentProps<typeof Breadcrumb>, 'Breadcrumb'>;
  /**
   * Styles override.
   */
  className?: string;
}

const List = styled('ol')`
  ${tokens.text('body2')};
  list-style: none;
  padding: 0;
`;

/**
 * A navigation system used to show a user's location in a site or app.
 *
 * {@link https://zeroheight.com/76c439891/p/58bd14-breadcrumbs Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-breadcrumbs Code documentation}
 *
 * @example
 * <Breadcrumbs>
 *   <Breadcrumbs.Item href="">Breadcrumb</Breadcrumbs.Item>
 *   <Breadcrumbs.Item>Page Title</Breadcrumbs.Item>
 * </Breadcrumbs>
 */
export const Breadcrumbs = ({
  children,
  className,
}: BreadcrumbsProps): JSX.Element => {
  const { breadcrumbs } = useStrings();
  return (
    <nav aria-label={breadcrumbs.label} className={className}>
      <List>{children}</List>
    </nav>
  );
};

Breadcrumbs.Item = Breadcrumb;
